const API_BASE_URL = process.env.REACT_APP_API_BASE_URL + '/api/v1';

class ApiService {
  // Auth endpoints
  async signup(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: userData.firstName,
          last_name: userData.lastName,
          email: userData.email,
          password: userData.password,
          phone: userData.phone,
          role: 'user' // Set as admin for admin panel access
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Signup failed');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error occurred');
    }
  }

  async login(email, password) {
    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error occurred');
    }
  }

  // Helper method to get auth token
  getAuthToken() {
    return localStorage.getItem('adminToken');
  }

  // Helper method to set auth token
  setAuthToken(token) {
    localStorage.setItem('adminToken', token);
  }

  // Helper method to remove auth token
  removeAuthToken() {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  }

  // Helper method to get current user
  getCurrentUser() {
    const userStr = localStorage.getItem('adminUser');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Helper method to set current user
  setCurrentUser(user) {
    localStorage.setItem('adminUser', JSON.stringify(user));
  }

  // Users endpoints
  async getUsers(page = 1, limit = 10) {
    try {
      const response = await this.apiCall(`/auth/users?page=${page}&limit=${limit}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch users');
    }
  }

  async createUser(userData) {
    try {
      const response = await this.apiCall('/auth/users', {
        method: 'POST',
        body: JSON.stringify({
          first_name: userData.firstName,
          last_name: userData.lastName,
          email: userData.email,
          phone: userData.phone,
          role: userData.role.toUpperCase(),
          is_active: userData.status === 'Active'
        }),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create user');
    }
  }

  async updateUser(userId, userData) {
    try {
      const response = await this.apiCall(`/auth/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({
          first_name: userData.firstName,
          last_name: userData.lastName,
          email: userData.email,
          phone: userData.phone,
          role: userData.role.toLowerCase(),
          is_active: userData.status === 'active'
        }),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update user');
    }
  }

  async deleteUser(userId) {
    try {
      const response = await this.apiCall(`/auth/users/${userId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete user');
    }
  }

  async updateUserPassword(userId, passwordData) {
    try {
      const response = await this.apiCall(`/auth/users/${userId}/password`, {
        method: 'PATCH',
        body: JSON.stringify({
          new_password: passwordData.newPassword,
        }),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update password');
    }
  }

  // Jobs endpoints
  async getJobs(page = 1, limit = 10) {
    try {
      const response = await this.apiCall(`/jobs?page=${page}&limit=${limit}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch jobs');
    }
  }

  // Public jobs endpoint (no auth required)
  async getPublicJobs(page = 1, limit = 10) {
    try {
      const response = await fetch(`${API_BASE_URL}/jobs?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch jobs');
    }
  }

  // Get individual job details (public endpoint)
  async getJobDetails(jobId) {
    try {
      const response = await fetch(`${API_BASE_URL}/jobs/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch job details');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch job details');
    }
  }

  // Submit job application (public endpoint)
  async submitJobApplication(applicationData) {
    try {
      const response = await fetch(`${API_BASE_URL}/jobs/applications/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit job application');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Failed to submit job application');
    }
  }

  // Get job applications (admin endpoint)
  async getJobApplications(page = 1, limit = 10) {
    try {
      
      const response = await this.apiCall(`/jobs/applications/?page=${page}&limit=${limit}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch job applications');
    }
  }

  // Delete job application (admin endpoint)
  async deleteJobApplication(applicationId) {
    try {
      const response = await this.apiCall(`/jobs/applications/${applicationId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete job application');
    }
  }

  // Update job application status (admin endpoint)
  async updateApplicationStatus(applicationId, status) {
    try {
      const response = await this.apiCall(`/jobs/applications/${applicationId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update application status');
    }
  }

  // Blog endpoints
  async createBlog(blogData) {
    try {
      const response = await this.apiCall('/blogs', {
        method: 'POST',
        body: JSON.stringify(blogData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create blog post');
    }
  }

  async getPublicBlogs(page = 1, limit = 10) {
    try {
      const response = await this.apiCall(`/blogs/?page=${page}&limit=${limit}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch public blogs');
    }
  }

  async getBlogDetail(identifier) {
    try {
      const response = await this.apiCall(`/blogs/${identifier}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch blog details');
    }
  }

  // Jobs - public available jobs list
  async getAvailableJobs() {
    try {
      const response = await this.apiCall('/jobs/availablejobs');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch available jobs');
    }
  }

  async getBlogs(page = 1, limit = 10) {
    try {
      const response = await this.apiCall(`/blogs/admin?page=${page}&limit=${limit}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch blogs');
    }
  }

  async updateBlog(blogId, blogData) {
    try {
      const response = await this.apiCall(`/blogs/${blogId}`, {
        method: 'PUT',
        body: JSON.stringify(blogData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update blog post');
    }
  }

  async deleteBlog(blogId) {
    try {
      const response = await this.apiCall(`/blogs/${blogId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete blog post');
    }
  }

  async createJob(jobData) {
    try {
      const response = await this.apiCall('/jobs', {
        method: 'POST',
        body: JSON.stringify({
          job_id: jobData.jobId,
          title: jobData.jobTitle,
          category: jobData.category,
          location: jobData.location,
          type: jobData.jobType,
          posted_date: jobData.postedDate,
          description: jobData.description,
          overview: jobData.overview,
          key_responsibilities: jobData.keyResponsibilities,
          qualifications: jobData.qualifications,
          remuneration: jobData.remuneration,
          why_join_us: jobData.whyJoinUs,
          is_active: jobData.status
        }),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create job');
    }
  }

  async updateJob(jobId, jobData) {
    try {
      const response = await this.apiCall(`/jobs/${jobId}`, {
        method: 'PUT',
        body: JSON.stringify({
          job_id: jobData.jobId,
          title: jobData.jobTitle,
          category: jobData.category,
          location: jobData.location,
          type: jobData.jobType,
          posted_date: jobData.postedDate,
          description: jobData.description,
          overview: jobData.overview,
          key_responsibilities: jobData.keyResponsibilities,
          qualifications: jobData.qualifications,
          remuneration: jobData.remuneration,
          why_join_us: jobData.whyJoinUs,
          is_active: jobData.status
        }),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update job');
    }
  }

  async deleteJob(jobId) {
    try {
      const response = await this.apiCall(`/jobs/${jobId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete job');
    }
  }

  // Generic API call with auth headers
  async apiCall(endpoint, options = {}) {
    const token = this.getAuthToken();
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...defaultOptions,
      ...options,
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired or invalid
        this.removeAuthToken();
        window.location.href = '/login';
        return;
      }
      
      const errorData = await response.json();
      throw new Error(errorData.detail || 'API request failed');
    }

    // Handle 204 No Content responses (like DELETE operations)
    if (response.status === 204) {
      return { success: true };
    }

    // Check if response has content before trying to parse JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      // For non-JSON responses, return success
      return { success: true };
    }
  }
}

const apiServiceInstance = new ApiService();
export default apiServiceInstance;
